import { useAuth } from '../../context/AuthContext';
import { getPhotoUrl } from '../../lib/photoUrl';

/**
 * Standard worker avatar for manager-facing surfaces.
 *
 * - Renders the worker's real profile picture when `profilePhoto` is set.
 * - Falls back to a first-letter monogram on a stable brand background.
 * - One consistent visual across:
 *     • Manager Dashboard (assigned/active worker widgets)
 *     • Applicants list & modals
 *     • Accepted / booked workers
 *     • Staff (current + history)
 *     • Shift Tracker / Logs (once assigned)
 *
 * Props:
 *   profilePhoto — raw path from backend (may be null / undefined)
 *   name         — worker's full name (used for monogram + alt text)
 *   size         — px number OR shorthand: 'sm' (32) / 'md' (48) / 'lg' (64)
 *   className    — extra classes to merge onto the outer element
 *   ringClass    — optional border ring class (e.g. 'ring-2 ring-emerald-500')
 *   testId       — data-testid on the wrapper
 */
const SIZE_MAP = { sm: 32, md: 48, lg: 64, xl: 80 };

export default function WorkerAvatar({
  profilePhoto,
  name = '',
  size = 'md',
  className = '',
  ringClass = '',
  testId,
}) {
  const { token } = useAuth();
  const px = typeof size === 'number' ? size : (SIZE_MAP[size] ?? SIZE_MAP.md);
  const style = { width: `${px}px`, height: `${px}px`, minWidth: `${px}px`, minHeight: `${px}px` };
  const src = getPhotoUrl(profilePhoto, token);
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const fontSize = Math.max(12, Math.round(px * 0.42));

  if (src) {
    return (
      <img
        src={src}
        alt={name || 'Worker'}
        style={style}
        className={`rounded-full object-cover shrink-0 bg-slate-200 ${ringClass} ${className}`}
        data-testid={testId || 'worker-avatar-image'}
        onError={(e) => {
          // On broken URL / expired token, hide the img so nothing shows
          // through as broken alt-text next to worker names.
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }

  // Fallback monogram — same look as before, kept brand-consistent
  return (
    <div
      style={{ ...style, fontSize: `${fontSize}px` }}
      className={`rounded-full bg-gradient-to-br from-[#0f1b3d] to-[#2a3f6d] text-white font-bold flex items-center justify-center shrink-0 ${ringClass} ${className}`}
      data-testid={testId || 'worker-avatar-fallback'}
      aria-label={name || 'Worker'}
    >
      {initial}
    </div>
  );
}
